import {
  ANCHORS,
  anchorToPoint,
  getBoundWithRotation,
  resolveEndpoint,
  ropeControl,
  ropePoints,
  seededJitter,
  sketchEllipse,
} from '../core/geometry';
import type { GfxController } from '../core/GfxController';
import { brushPoints } from '../core/SurfaceModel';
import {
  type BrushProps,
  type CircleProps,
  type ConnectorProps,
  type FrameProps,
  type GfxElement,
  type HighlightProps,
  type IVec,
  type PinProps,
  type RedactProps,
  type RopeProps,
  type ShapeProps,
  type StampProps,
  type TapeProps,
} from '../core/types';

const CORK_TILE = 96;

export const CONTENT_TYPES = ['frame', 'group', 'shape', 'connector', 'rope', 'highlight'];
export const ANNOTATION_TYPES = ['pin', 'tape', 'circle', 'brush', 'stamp', 'redact'];

/** 画布内容 + 标注 + 叠加层渲染器（借鉴 AFFiNE 的多 canvas 图层）。 */
export class CanvasRenderer {
  private content: HTMLCanvasElement;
  private annotation: HTMLCanvasElement;
  private overlay: HTMLCanvasElement;
  private contentCtx: CanvasRenderingContext2D;
  private annotationCtx: CanvasRenderingContext2D;
  private overlayCtx: CanvasRenderingContext2D;
  private dpr = 1;
  private corkPattern: CanvasPattern | null = null;

  constructor(
    content: HTMLCanvasElement,
    annotation: HTMLCanvasElement,
    overlay: HTMLCanvasElement,
    private controller: GfxController
  ) {
    this.content = content;
    this.annotation = annotation;
    this.overlay = overlay;
    this.contentCtx = content.getContext('2d')!;
    this.annotationCtx = annotation.getContext('2d')!;
    this.overlayCtx = overlay.getContext('2d')!;
  }

  resize(width: number, height: number): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const canvas of [this.content, this.annotation, this.overlay]) {
      canvas.width = Math.max(1, Math.floor(width * this.dpr));
      canvas.height = Math.max(1, Math.floor(height * this.dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
    }
  }

  render(): void {
    this.renderContent();
    this.renderAnnotations();
    this.renderOverlay();
  }

  private prepare(ctx: CanvasRenderingContext2D): void {
    const { viewport } = this.controller;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, viewport.width, viewport.height);
  }

  // ── 内容层（卡片下方）─────────────────────────────
  private renderContent(): void {
    const { viewport } = this.controller;
    const ctx = this.contentCtx;
    this.prepare(ctx);
    this.drawBackground(ctx, viewport.width, viewport.height);
    this.drawOrigin(ctx);

    const visible = this.controller.surface.query(viewport.overscanBounds(0.25));
    const elements = visible
      .filter(el => CONTENT_TYPES.includes(el.type))
      .sort((a, b) => CONTENT_TYPES.indexOf(a.type) - CONTENT_TYPES.indexOf(b.type) || (a.z < b.z ? -1 : 1));
    for (const el of elements) this.drawElement(ctx, el);
  }

  // ── 标注层（卡片上方：图钉 / 胶带 / 圈注 / 手绘 / 印章 / 遮挡）──
  private renderAnnotations(): void {
    const { viewport } = this.controller;
    const ctx = this.annotationCtx;
    this.prepare(ctx);
    const visible = this.controller.surface.query(viewport.overscanBounds(0.25));
    const elements = visible
      .filter(el => ANNOTATION_TYPES.includes(el.type))
      .sort((a, b) => ANNOTATION_TYPES.indexOf(a.type) - ANNOTATION_TYPES.indexOf(b.type) || (a.z < b.z ? -1 : 1));
    for (const el of elements) this.drawElement(ctx, el);
  }

  private drawBackground(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const style = this.controller.boardStyle;

    if (style === 'corkboard') {
      ctx.fillStyle = '#c8a878';
      ctx.fillRect(0, 0, w, h);
      const pattern = this.ensureCorkPattern(ctx);
      if (pattern) {
        const { viewport } = this.controller;
        const offsetX = -((viewport.viewportX * viewport.zoom) % CORK_TILE);
        const offsetY = -((viewport.viewportY * viewport.zoom) % CORK_TILE);
        ctx.save();
        ctx.translate(offsetX, offsetY);
        ctx.fillStyle = pattern;
        ctx.fillRect(-offsetX, -offsetY, w, h);
        ctx.restore();
      }
      const vignette = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.75);
      vignette.addColorStop(0, 'rgba(80,55,25,0)');
      vignette.addColorStop(1, 'rgba(70,45,20,0.22)');
      ctx.fillStyle = vignette;
      ctx.fillRect(0, 0, w, h);
      return;
    }

    // 默认纯白纸面
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    if (style === 'grid') this.drawGrid(ctx);
  }

  private ensureCorkPattern(ctx: CanvasRenderingContext2D): CanvasPattern | null {
    if (this.corkPattern) return this.corkPattern;
    const tile = document.createElement('canvas');
    tile.width = CORK_TILE;
    tile.height = CORK_TILE;
    const tctx = tile.getContext('2d');
    if (!tctx) return null;
    tctx.fillStyle = '#c9a87c';
    tctx.fillRect(0, 0, CORK_TILE, CORK_TILE);
    for (let i = 0; i < 1400; i++) {
      const x = Math.random() * CORK_TILE;
      const y = Math.random() * CORK_TILE;
      const r = Math.random() * 1.9 + 0.3;
      const dark = Math.random() < 0.52;
      tctx.fillStyle = dark ? 'rgba(146,106,66,0.34)' : 'rgba(236,212,178,0.42)';
      tctx.beginPath();
      tctx.arc(x, y, r, 0, Math.PI * 2);
      tctx.fill();
    }
    // 细纤维
    for (let i = 0; i < 60; i++) {
      const x = Math.random() * CORK_TILE;
      const y = Math.random() * CORK_TILE;
      tctx.strokeStyle = Math.random() < 0.5 ? 'rgba(146,106,66,0.2)' : 'rgba(236,212,178,0.22)';
      tctx.lineWidth = 0.6;
      tctx.beginPath();
      tctx.moveTo(x, y);
      tctx.lineTo(x + (Math.random() - 0.5) * 9, y + (Math.random() - 0.5) * 9);
      tctx.stroke();
    }
    this.corkPattern = ctx.createPattern(tile, 'repeat');
    return this.corkPattern;
  }

  private drawGrid(ctx: CanvasRenderingContext2D): void {
    const { viewport } = this.controller;
    const zoom = viewport.zoom;
    let spacing = 40;
    while (spacing * zoom < 24) spacing *= 2;
    while (spacing * zoom > 120) spacing /= 2;

    const start = viewport.screenToModel([0, 0]);
    const end = viewport.screenToModel([viewport.width, viewport.height]);
    const x0 = Math.floor(start[0] / spacing) * spacing;
    const y0 = Math.floor(start[1] / spacing) * spacing;
    const radius = Math.max(0.6, Math.min(1.6, zoom));

    ctx.fillStyle = 'rgba(120, 113, 108, 0.28)';
    for (let x = x0; x <= end[0]; x += spacing) {
      for (let y = y0; y <= end[1]; y += spacing) {
        const p = viewport.modelToScreen([x, y]);
        ctx.beginPath();
        ctx.arc(p[0], p[1], radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawOrigin(ctx: CanvasRenderingContext2D): void {
    // 纯白纸面不画原点辅助框，保持空白干净
    if (this.controller.boardStyle === 'paper') return;
    const { viewport } = this.controller;
    const origin = viewport.modelToScreen([0, 0]);
    const size = 180 * viewport.zoom;
    ctx.strokeStyle = 'rgba(30, 58, 95, 0.16)';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 6]);
    ctx.strokeRect(origin[0], origin[1], size, size);
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(120, 113, 108, 0.75)';
    ctx.font = '11px ui-monospace, monospace';
    ctx.fillText('基准原点', origin[0] + 6, origin[1] + 16);
  }

  private drawElement(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    switch (el.type) {
      case 'connector':
        this.drawConnector(ctx, el);
        return;
      case 'rope':
        this.drawRope(ctx, el);
        return;
      case 'pin':
        this.drawPin(ctx, el);
        return;
      case 'circle':
        this.drawCircle(ctx, el);
        return;
      case 'tape':
        this.drawTape(ctx, el);
        return;
      case 'brush':
        this.drawBrush(ctx, el);
        return;
      case 'stamp':
        this.drawStamp(ctx, el);
        return;
      case 'highlight':
        this.drawHighlight(ctx, el);
        return;
      case 'redact':
        this.drawRedact(ctx, el);
        return;
      default:
        break;
    }

    const bound = getBoundWithRotation(el);
    const topLeft = this.controller.viewport.modelToScreen([bound.x, bound.y]);
    const zoom = this.controller.viewport.zoom;
    const w = bound.w * zoom;
    const h = bound.h * zoom;
    if (w < 1 || h < 1) return;

    ctx.save();
    ctx.translate(topLeft[0], topLeft[1]);
    if (el.rotate) {
      ctx.translate(w / 2, h / 2);
      ctx.rotate((el.rotate * Math.PI) / 180);
      ctx.translate(-w / 2, -h / 2);
    }
    if (el.type === 'shape') this.drawShape(ctx, el, w, h);
    else if (el.type === 'frame') this.drawFrame(ctx, el, w, h);
    else if (el.type === 'group') this.drawGroup(ctx, w, h);
    ctx.restore();
  }

  private drawShape(ctx: CanvasRenderingContext2D, el: GfxElement, w: number, h: number): void {
    const props = el.props as unknown as ShapeProps;
    ctx.fillStyle = props.fill || '#ffffff';
    ctx.strokeStyle = props.stroke || '#475569';
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    if (props.shape === 'ellipse') ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
    else if (props.shape === 'diamond') {
      ctx.moveTo(w / 2, 0);
      ctx.lineTo(w, h / 2);
      ctx.lineTo(w / 2, h);
      ctx.lineTo(0, h / 2);
      ctx.closePath();
    } else if (props.shape === 'triangle') {
      ctx.moveTo(w / 2, 0);
      ctx.lineTo(w, h);
      ctx.lineTo(0, h);
      ctx.closePath();
    } else if (props.shape === 'hexagon') {
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI / 3) * i - Math.PI / 2;
        const px = w / 2 + Math.cos(angle) * (w / 2);
        const py = h / 2 + Math.sin(angle) * (h / 2);
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    } else if (props.shape === 'star') {
      const spikes = 5;
      for (let i = 0; i < spikes * 2; i++) {
        const radius = i % 2 === 0 ? 1 : 0.45;
        const angle = (Math.PI / spikes) * i - Math.PI / 2;
        const px = w / 2 + Math.cos(angle) * (w / 2) * radius;
        const py = h / 2 + Math.sin(angle) * (h / 2) * radius;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
    } else if (props.shape === 'arrow') {
      const headW = Math.min(w * 0.42, h * 0.62);
      const shaftH = h * 0.42;
      ctx.moveTo(0, h / 2 - shaftH / 2);
      ctx.lineTo(w - headW, h / 2 - shaftH / 2);
      ctx.lineTo(w - headW, 0);
      ctx.lineTo(w, h / 2);
      ctx.lineTo(w - headW, h);
      ctx.lineTo(w - headW, h / 2 + shaftH / 2);
      ctx.lineTo(0, h / 2 + shaftH / 2);
      ctx.closePath();
    } else if (props.shape === 'rounded') ctx.roundRect(0, 0, w, h, Math.min(16, w / 4, h / 4));
    else ctx.rect(0, 0, w, h);
    ctx.fill();
    ctx.stroke();

    if (props.text) {
      ctx.fillStyle = props.textColor || '#1f2937';
      ctx.font = `500 ${Math.max(9, Math.min(18, 14 * this.controller.viewport.zoom))}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      this.wrapText(ctx, props.text, w / 2, h / 2, w - 16, 18);
    }
  }

  private drawFrame(ctx: CanvasRenderingContext2D, el: GfxElement, w: number, h: number): void {
    const props = el.props as unknown as FrameProps;
    const color = props.color || '#78716c';
    ctx.fillStyle = 'rgba(255,255,255,0.28)';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash([8, 6]);
    ctx.beginPath();
    ctx.roundRect(0, 0, w, h, 10);
    ctx.fill();
    ctx.stroke();
    ctx.setLineDash([]);
    if (props.title) {
      ctx.fillStyle = color;
      ctx.font = `600 ${Math.max(10, Math.min(16, 13 * this.controller.viewport.zoom))}px sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(props.title, 8, -6);
    }
  }

  private drawGroup(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    ctx.strokeStyle = 'rgba(120, 113, 108, 0.4)';
    ctx.setLineDash([4, 4]);
    ctx.strokeRect(0, 0, w, h);
    ctx.setLineDash([]);
  }

  private drawConnector(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as ConnectorProps;
    const points = this.controller.connectorPoints(props.source, props.target, props.mode);
    if (points.length < 2) return;
    const { viewport } = this.controller;
    const screen = points.map(p => viewport.modelToScreen(p));
    const color = props.stroke || '#78716c';

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(1, 2 * viewport.zoom);
    if (props.dashed) ctx.setLineDash([8, 6]);
    ctx.beginPath();
    ctx.moveTo(screen[0][0], screen[0][1]);
    for (let i = 1; i < screen.length; i++) ctx.lineTo(screen[i][0], screen[i][1]);
    ctx.stroke();
    ctx.setLineDash([]);
    this.drawArrow(ctx, screen[screen.length - 2], screen[screen.length - 1], color);
    if (props.label) this.drawTag(ctx, props.label, screen[Math.floor(screen.length / 2)]);
    ctx.restore();
  }

  /** 联系绳索：扭结绳体 + 端结（系在卡片图钉处）。 */
  private drawRope(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as RopeProps;
    const { viewport } = this.controller;
    const lookup = this.controller.surface.lookup;
    const from = resolveEndpoint(props.source, lookup);
    const to = resolveEndpoint(props.target, lookup);
    const sag = props.sag ?? 0.14;
    const zoom = viewport.zoom;
    const pts = ropePoints(from, to, sag, 44).map(p => viewport.modelToScreen(p));
    if (pts.length < 2) return;

    const w = Math.max(1.15, (props.width ?? 2.5) * zoom);
    const color = props.color || '#b3261e';
    const dark = shadeHex(color, -0.42);
    const light = shadeHex(color, 0.42);

    const path = () => {
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    };

    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    // 投到板面上的阴影
    ctx.strokeStyle = 'rgba(40, 30, 15, 0.16)';
    ctx.lineWidth = w * 1.7;
    path();
    ctx.save();
    ctx.translate(0, w * 1.1);
    ctx.stroke();
    ctx.restore();

    // 绳芯（深色）
    ctx.strokeStyle = dark;
    ctx.lineWidth = w;
    path();
    ctx.stroke();

    // 主体色
    ctx.strokeStyle = color;
    ctx.lineWidth = Math.max(0.7, w * 0.72);
    path();
    ctx.stroke();

    // 高光纤维（偏上一侧）
    ctx.strokeStyle = light;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = Math.max(0.5, w * 0.26);
    ctx.save();
    ctx.translate(0, -w * 0.22);
    path();
    ctx.stroke();
    ctx.restore();
    ctx.globalAlpha = 1;

    // 扭转纹理：沿曲线交错斜切
    ctx.strokeStyle = shadeHex(color, -0.2);
    ctx.lineWidth = Math.max(0.6, w * 0.5);
    for (let i = 1; i < pts.length - 1; i += 2) {
      const prev = pts[i - 1];
      const next = pts[i + 1];
      const p = pts[i];
      const tx = next[0] - prev[0];
      const ty = next[1] - prev[1];
      const len = Math.hypot(tx, ty) || 1;
      const nx = -ty / len;
      const ny = tx / len;
      const side = i % 4 === 1 ? 1 : -1;
      ctx.beginPath();
      ctx.moveTo(p[0] - nx * w * 0.5, p[1] - ny * w * 0.5);
      ctx.lineTo(p[0] + nx * w * 0.72 * side + (tx / len) * w * 0.35, p[1] + ny * w * 0.72 * side + (ty / len) * w * 0.35);
      ctx.stroke();
    }
    ctx.restore();

    // 两端绳结
    this.drawKnot(ctx, pts[0], w, color);
    this.drawKnot(ctx, pts[pts.length - 1], w, color);

    if (props.label) {
      const mid = pts[Math.floor(pts.length / 2)];
      ctx.save();
      ctx.translate(0, w * 1.4);
      this.drawTag(ctx, props.label, mid);
      ctx.restore();
    }
  }

  /** 绳结：绕在图钉上的小环。 */
  private drawKnot(ctx: CanvasRenderingContext2D, p: IVec, w: number, color: string): void {
    const r = Math.max(2.2, w * 1.7);
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(p[0], p[1], r, r * 0.82, -0.35, 0, Math.PI * 2);
    ctx.fillStyle = shadeHex(color, -0.3);
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(p[0] - r * 0.12, p[1] - r * 0.16, r * 0.74, r * 0.6, -0.35, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(p[0], p[1], r * 0.42, r * 0.32, -0.35, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0,0,0,0.28)';
    ctx.fill();
    ctx.restore();
  }

  /** 图钉：金属针脚 + 塑料球头（真实推钉质感）。 */
  private drawPin(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as PinProps;
    const { viewport } = this.controller;
    const tip = viewport.modelToScreen([el.x + el.w / 2, el.y + el.h / 2]);
    const r = Math.max(3.6, (el.w / 2) * viewport.zoom);
    const color = props.color || '#b3261e';

    if (props.kind === 'clip') {
      ctx.save();
      ctx.strokeStyle = shadeHex(color, -0.1);
      ctx.lineWidth = Math.max(1.5, 2.4 * viewport.zoom);
      ctx.beginPath();
      ctx.roundRect(tip[0] - r * 0.55, tip[1] - r, r * 1.1, r * 2, r * 0.5);
      ctx.stroke();
      ctx.restore();
      return;
    }

    const headX = tip[0] + r * 0.2;
    const headY = tip[1] - r * 1.15;

    // 针脚（先画阴影，再画金属）
    ctx.save();
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(30, 35, 45, 0.3)';
    ctx.lineWidth = Math.max(1, r * 0.36);
    ctx.beginPath();
    ctx.moveTo(headX + r * 0.06, headY + r * 0.25);
    ctx.lineTo(tip[0] + r * 0.4, tip[1] + r * 0.7);
    ctx.stroke();

    ctx.strokeStyle = '#c6cbd3';
    ctx.lineWidth = Math.max(0.9, r * 0.28);
    ctx.beginPath();
    ctx.moveTo(headX, headY + r * 0.2);
    ctx.lineTo(tip[0] + r * 0.3, tip[1] + r * 0.45);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(255,255,255,0.85)';
    ctx.lineWidth = Math.max(0.4, r * 0.1);
    ctx.beginPath();
    ctx.moveTo(headX - r * 0.08, headY + r * 0.22);
    ctx.lineTo(tip[0] + r * 0.22, tip[1] + r * 0.42);
    ctx.stroke();
    ctx.restore();

    // 金属底座
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(headX, headY + r * 0.68, r * 0.66, r * 0.3, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#9fa6b0';
    ctx.fill();
    ctx.beginPath();
    ctx.ellipse(headX, headY + r * 0.58, r * 0.58, r * 0.26, 0, 0, Math.PI * 2);
    ctx.fillStyle = '#e6e9ee';
    ctx.fill();
    ctx.restore();

    // 塑料球头
    ctx.save();
    ctx.beginPath();
    ctx.arc(headX, headY + r * 0.15, r * 1.05, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(30, 35, 45, 0.14)';
    ctx.fill();
    const grad = ctx.createRadialGradient(headX - r * 0.38, headY - r * 0.42, r * 0.12, headX, headY, r * 1.2);
    grad.addColorStop(0, shadeHex(color, 0.62));
    grad.addColorStop(0.45, color);
    grad.addColorStop(1, shadeHex(color, -0.42));
    ctx.beginPath();
    ctx.arc(headX, headY, r, 0, Math.PI * 2);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(headX, headY, r, 0.18 * Math.PI, 0.92 * Math.PI);
    ctx.strokeStyle = 'rgba(0,0,0,0.16)';
    ctx.lineWidth = r * 0.2;
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(headX - r * 0.34, headY - r * 0.4, r * 0.3, r * 0.18, -0.6, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fill();
    ctx.restore();
  }

  /** 圈注：手绘椭圆。 */
  private drawCircle(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as CircleProps;
    const { viewport } = this.controller;
    const bound = { x: el.x, y: el.y, w: el.w, h: el.h };
    const points = sketchEllipse(bound, props.seed ?? 1, 72).map(p => viewport.modelToScreen(p));
    ctx.save();
    ctx.strokeStyle = props.color || '#b3261e';
    ctx.lineWidth = Math.max(1.2, (props.strokeWidth ?? 2.5) * viewport.zoom);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (props.dashed) ctx.setLineDash([10, 7]);
    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);
    for (let i = 1; i < points.length; i++) ctx.lineTo(points[i][0], points[i][1]);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
    if (props.text) {
      const top = viewport.modelToScreen([el.x + el.w / 2, el.y]);
      ctx.save();
      ctx.font = `700 ${Math.max(10, 13 * viewport.zoom)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillStyle = props.color || '#b3261e';
      ctx.fillText(props.text, top[0], top[1] - 5);
      ctx.restore();
    }
  }

  /** 胶带：半透明带轻微边缘抖动。 */
  private drawTape(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as TapeProps;
    const { viewport } = this.controller;
    const topLeft = viewport.modelToScreen([el.x, el.y]);
    const w = el.w * viewport.zoom;
    const h = el.h * viewport.zoom;
    const jitter = seededJitter(props.seed ?? 1, 6, h * 0.14);

    ctx.save();
    ctx.translate(topLeft[0] + w / 2, topLeft[1] + h / 2);
    ctx.rotate((el.rotate * Math.PI) / 180);
    ctx.translate(-w / 2, -h / 2);
    ctx.globalAlpha = 0.78;
    ctx.fillStyle = props.color || '#f3e7c9';
    ctx.beginPath();
    ctx.moveTo(-w * 0.06 + jitter[0], jitter[1]);
    ctx.lineTo(w + w * 0.06 + jitter[2], jitter[3]);
    ctx.lineTo(w + w * 0.06 + jitter[4], h + jitter[5]);
    ctx.lineTo(-w * 0.06 + jitter[3], h + jitter[0]);
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, h * 0.18, w, h * 0.16);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(120,100,70,0.18)';
    ctx.lineWidth = 0.6;
    ctx.stroke();
    ctx.restore();
  }

  private drawHighlight(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as HighlightProps;
    const { viewport } = this.controller;
    const p = viewport.modelToScreen([el.x, el.y]);
    const w = el.w * viewport.zoom;
    const h = el.h * viewport.zoom;
    if (w < 1 || h < 1) return;
    ctx.save();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = props.color || '#fde047';
    ctx.beginPath();
    ctx.roundRect(p[0], p[1], w, h, Math.min(6, h / 4));
    ctx.fill();
    ctx.restore();
  }

  private drawRedact(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as RedactProps;
    const { viewport } = this.controller;
    const p = viewport.modelToScreen([el.x, el.y]);
    const w = el.w * viewport.zoom;
    const h = el.h * viewport.zoom;
    if (w < 1 || h < 1) return;
    ctx.save();
    ctx.fillStyle = props.color || '#111827';
    ctx.beginPath();
    ctx.roundRect(p[0], p[1], w, h, Math.min(3, h / 3));
    ctx.fill();
    ctx.restore();
  }

  private drawArrow(ctx: CanvasRenderingContext2D, from: IVec, to: IVec, color: string): void {
    const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
    const size = 9;
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(to[0], to[1]);
    ctx.lineTo(to[0] - size * Math.cos(angle - Math.PI / 7), to[1] - size * Math.sin(angle - Math.PI / 7));
    ctx.lineTo(to[0] - size * Math.cos(angle + Math.PI / 7), to[1] - size * Math.sin(angle + Math.PI / 7));
    ctx.closePath();
    ctx.fill();
  }

  /** 自由手绘：平滑折线（铅笔 / 马克笔）。 */
  private drawBrush(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as BrushProps;
    const { viewport } = this.controller;
    const pts = brushPoints(el).map(p => viewport.modelToScreen(p));
    if (pts.length < 2) return;
    const w = Math.max(0.7, (props.width ?? 3) * viewport.zoom);
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (props.mode === 'marker') {
      ctx.globalAlpha = 0.42;
      ctx.strokeStyle = props.color || '#fde047';
      ctx.lineWidth = w * 2;
    } else {
      ctx.strokeStyle = props.color || '#b3261e';
      ctx.lineWidth = w;
    }
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i][0] + pts[i + 1][0]) / 2;
      const my = (pts[i][1] + pts[i + 1][1]) / 2;
      ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
    }
    ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]);
    ctx.stroke();
    if (props.mode === 'pencil') {
      // 铅笔颗粒：沿路径散布小点
      const jitter = seededJitter(props.seed ?? 1, 40, 1.2);
      ctx.globalAlpha = 0.35;
      ctx.fillStyle = props.color || '#b3261e';
      for (let i = 0; i < pts.length; i += 2) {
        const p = pts[i];
        ctx.beginPath();
        ctx.arc(p[0] + (jitter[i % jitter.length] ?? 0), p[1] + (jitter[(i + 7) % jitter.length] ?? 0), w * 0.34, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.restore();
  }

  /** 印章：双环 + 文字（可圆形 / 矩形，带做旧）。 */
  private drawStamp(ctx: CanvasRenderingContext2D, el: GfxElement): void {
    const props = el.props as unknown as StampProps;
    const { viewport } = this.controller;
    const topLeft = viewport.modelToScreen([el.x, el.y]);
    const w = el.w * viewport.zoom;
    const h = el.h * viewport.zoom;
    if (w < 8 || h < 8) return;
    const color = props.color || '#b3261e';

    ctx.save();
    ctx.translate(topLeft[0] + w / 2, topLeft[1] + h / 2);
    ctx.rotate(((el.rotate || -8) * Math.PI) / 180);
    ctx.globalAlpha = 0.82;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    if (props.shape === 'round') {
      const r = Math.min(w, h) / 2;
      ctx.lineWidth = Math.max(1.4, r * 0.075);
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.92, 0, Math.PI * 2);
      ctx.stroke();
      ctx.lineWidth = Math.max(1, r * 0.04);
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.78, 0, Math.PI * 2);
      ctx.stroke();
      const chars = [...(props.text || '证物')];
      const size = Math.max(8, Math.min(r * 0.44, (r * 1.5) / Math.max(1, chars.length) * 1.4));
      ctx.font = `700 ${size}px "Noto Sans SC", system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(chars.join(''), 0, 0);
    } else {
      const rx = w * 0.46;
      const ry = h * 0.32;
      ctx.lineWidth = Math.max(1.6, ry * 0.14);
      ctx.beginPath();
      ctx.roundRect(-rx, -ry, rx * 2, ry * 2, ry * 0.2);
      ctx.stroke();
      const size = Math.max(9, Math.min(ry * 0.8, (rx * 1.7) / Math.max(1, (props.text || '证物').length)));
      ctx.font = `700 ${size}px "Noto Sans SC", system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(props.text || '证物', 0, 0);
    }

    // 做旧：随机缺墨点
    const jitter = seededJitter(props.seed ?? 3, 60, Math.max(w, h) * 0.4);
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < 40; i++) {
      const x = (jitter[i % jitter.length] ?? 0) * 0.9;
      const y = (jitter[(i + 11) % jitter.length] ?? 0) * 0.9;
      ctx.beginPath();
      ctx.arc(x, y, Math.max(0.6, w * 0.008), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  private drawTag(ctx: CanvasRenderingContext2D, label: string, at: IVec): void {
    ctx.save();
    ctx.font = '600 11px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const metrics = ctx.measureText(label);
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.strokeStyle = '#d8d4ca';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(at[0] - metrics.width / 2 - 5, at[1] - 10, metrics.width + 10, 19, 4);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#3f4247';
    ctx.fillText(label, at[0], at[1]);
    ctx.restore();
  }

  private wrapText(
    ctx: CanvasRenderingContext2D,
    text: string,
    cx: number,
    cy: number,
    maxWidth: number,
    lineHeight: number
  ): void {
    const lines = wrapLines(ctx, text, maxWidth);
    const startY = cy - ((lines.length - 1) * lineHeight) / 2;
    lines.slice(0, 8).forEach((line, i) => ctx.fillText(line, cx, startY + i * lineHeight));
  }

  // ── 叠加层 ────────────────────────────────────────────
  private renderOverlay(): void {
    const ctx = this.overlayCtx;
    const { viewport, selection } = this.controller;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, viewport.width, viewport.height);

    const marquee = this.controller.activeMarquee;
    if (marquee) {
      const p = viewport.modelToScreen([marquee.x, marquee.y]);
      ctx.fillStyle = 'rgba(30, 58, 95, 0.07)';
      ctx.strokeStyle = 'rgba(30, 58, 95, 0.75)';
      ctx.lineWidth = 1;
      ctx.fillRect(p[0], p[1], marquee.w * viewport.zoom, marquee.h * viewport.zoom);
      ctx.strokeRect(p[0], p[1], marquee.w * viewport.zoom, marquee.h * viewport.zoom);
    }

    // 绘制预览（圈注 / 荧光）
    const drawPreview = this.controller.drawPreview;
    if (drawPreview) {
      const p = viewport.modelToScreen([drawPreview.x, drawPreview.y]);
      const w = drawPreview.w * viewport.zoom;
      const h = drawPreview.h * viewport.zoom;
      const kind = this.controller.tool;
      if (kind === 'circle') {
        const points = sketchEllipse(drawPreview, Date.now() % 1000, 64).map(pt => viewport.modelToScreen(pt));
        ctx.strokeStyle = '#b3261e';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(points[0][0], points[0][1]);
        for (let i = 1; i < points.length; i++) ctx.lineTo(points[i][0], points[i][1]);
        ctx.stroke();
      } else {
        ctx.fillStyle = 'rgba(253, 224, 71, 0.5)';
        ctx.fillRect(p[0], p[1], w, h);
      }
    }

    const bound = selection.bound;
    if (bound && selection.size > 0) {
      const p = viewport.modelToScreen([bound.x, bound.y]);
      const w = bound.w * viewport.zoom;
      const h = bound.h * viewport.zoom;
      ctx.strokeStyle = '#1e3a5f';
      ctx.lineWidth = 1.25;
      ctx.strokeRect(p[0], p[1], w, h);

      if (selection.size === 1 && this.controller.tool === 'select') {
        for (const anchor of HANDLE_ANCHORS) {
          const hx = p[0] + w * anchor[0];
          const hy = p[1] + h * anchor[1];
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#1e3a5f';
          ctx.lineWidth = 1.25;
          ctx.beginPath();
          ctx.rect(hx - HANDLE_SIZE / 2, hy - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
          ctx.fill();
          ctx.stroke();
        }
      }
    }

    for (const el of selection.elements) {
      if (el.type === 'connector') {
        const props = el.props as unknown as ConnectorProps;
        const points = this.controller.connectorPoints(props.source, props.target, props.mode);
        const screen = points.map(p => viewport.modelToScreen(p));
        ctx.strokeStyle = 'rgba(30, 58, 95, 0.5)';
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.moveTo(screen[0][0], screen[0][1]);
        for (let i = 1; i < screen.length; i++) ctx.lineTo(screen[i][0], screen[i][1]);
        ctx.stroke();
      }
      if (el.type === 'rope') {
        const props = el.props as unknown as RopeProps;
        const model = ropePoints(
          resolveEndpoint(props.source, this.controller.surface.lookup),
          resolveEndpoint(props.target, this.controller.surface.lookup),
          props.sag ?? 0.14,
          24
        );
        const screen = model.map(p => viewport.modelToScreen(p));
        ctx.strokeStyle = 'rgba(30, 58, 95, 0.35)';
        ctx.lineWidth = 7;
        ctx.beginPath();
        ctx.moveTo(screen[0][0], screen[0][1]);
        for (let i = 1; i < screen.length; i++) ctx.lineTo(screen[i][0], screen[i][1]);
        ctx.stroke();
      }
    }

    // 绳索 / 连线预览
    const preview = this.controller.connectorPreview;
    if (preview) {
      const isRope = this.controller.tool === 'rope';
      const screen = isRope
        ? ropePoints(preview[0], preview[1], 0.14, 24).map(p => viewport.modelToScreen(p))
        : preview.map(p => viewport.modelToScreen(p));
      ctx.strokeStyle = isRope ? '#b3261e' : '#1e3a5f';
      ctx.lineWidth = isRope ? 2.5 : 2;
      if (!isRope) ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(screen[0][0], screen[0][1]);
      for (let i = 1; i < screen.length; i++) ctx.lineTo(screen[i][0], screen[i][1]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    if ((this.controller.tool === 'connector' || this.controller.tool === 'rope') && selection.size === 1) {
      const el = selection.elements[0];
      if (el && el.type !== 'connector' && el.type !== 'rope' && el.type !== 'pin') {
        const b = { x: el.x, y: el.y, w: el.w, h: el.h };
        for (const anchor of ANCHORS.slice(0, 4)) {
          const point = anchorToPoint(b, anchor);
          const p = viewport.modelToScreen(point);
          ctx.fillStyle = '#1e3a5f';
          ctx.beginPath();
          ctx.arc(p[0], p[1], 4.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }

    // 手绘实时笔迹
    const ink = this.controller.inkPreview;
    if (ink && ink.length >= 2) {
      const pts = ink.map(p => viewport.modelToScreen(p));
      ctx.strokeStyle = this.controller.brushColor;
      ctx.lineWidth = Math.max(1, (this.controller.brushMode === 'marker' ? 6 : 3) * viewport.zoom);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (this.controller.brushMode === 'marker') ctx.globalAlpha = 0.45;
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // 连接线 / 绳索端点手柄
    if (selection.size === 1) {
      const sel = selection.elements[0];
      if (sel && (sel.type === 'connector' || sel.type === 'rope')) {
        const props = sel.props as unknown as ConnectorProps & { sag?: number };
        const ends: IVec[] = [
          resolveEndpoint(props.source, this.controller.surface.lookup),
          resolveEndpoint(props.target, this.controller.surface.lookup),
        ];
        for (const point of ends) {
          const p = viewport.modelToScreen(point);
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#1e3a5f';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.arc(p[0], p[1], 6, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        }
      }
    }

    // 端点拖拽吸附目标高亮
    const snapId = this.controller.endpointSnapId;
    if (snapId) {
      const target = this.controller.surface.get(snapId);
      if (target) {
        const b = getBoundWithRotation(target);
        const p = viewport.modelToScreen([b.x, b.y]);
        ctx.strokeStyle = 'rgba(179, 38, 30, 0.9)';
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(p[0] - 3, p[1] - 3, b.w * viewport.zoom + 6, b.h * viewport.zoom + 6);
        ctx.setLineDash([]);
      }
    }

    const hoverId = this.controller.hoverId;
    if (hoverId && !selection.has(hoverId) && this.controller.tool === 'select') {
      const el = this.controller.surface.get(hoverId);
      if (el && el.type !== 'connector' && el.type !== 'rope') {
        const b = getBoundWithRotation(el);
        const p = viewport.modelToScreen([b.x, b.y]);
        ctx.strokeStyle = 'rgba(120, 113, 108, 0.55)';
        ctx.lineWidth = 1;
        ctx.strokeRect(p[0], p[1], b.w * viewport.zoom, b.h * viewport.zoom);
      }
    }
  }
}

export const HANDLE_SIZE = 8;

/** 十六进制色明暗调整（amount > 0 变亮，< 0 变暗）。 */
function shadeHex(hex: string, amount: number): string {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value.split('').map(c => c + c).join('')
      : value.padEnd(6, '0').slice(0, 6);
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return hex;
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;
  if (amount >= 0) {
    r = Math.round(r + (255 - r) * amount);
    g = Math.round(g + (255 - g) * amount);
    b = Math.round(b + (255 - b) * amount);
  } else {
    r = Math.round(r * (1 + amount));
    g = Math.round(g * (1 + amount));
    b = Math.round(b * (1 + amount));
  }
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
}
export const HANDLE_ANCHORS: IVec[] = [
  [0, 0],
  [0.5, 0],
  [1, 0],
  [1, 0.5],
  [1, 1],
  [0.5, 1],
  [0, 1],
  [0, 0.5],
];

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const paragraphs = text.split('\n');
  const lines: string[] = [];
  for (const paragraph of paragraphs) {
    const words = paragraph.split(' ');
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        lines.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    lines.push(line);
  }
  return lines;
}

export { ropeControl };